import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";
import { i18n, setLocale } from "../i18n";

Vue.use(VueRouter);

export type RouteAuthConfig = { allowAnonymous?: boolean; roles?: string[] };
export type PortalRouteConfig = { auth?: RouteAuthConfig } & RouteConfig;

export const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
});

import { Auth, PortalModule } from "@/@core";
import { Maintenance } from "../maintenance";

export function initRouter(...modules: PortalModule[]) {
  const routes = [
    ...modules.reduce((routes, module) => {
      routes.push(
        ...module.routes.map(r => {
          if (r.auth) {
            protectByRouteAuthConfig(r, r.auth);
          }
          return r;
        })
      );
      return routes;
    }, [] as PortalRouteConfig[]),
    {
      path: "/*",
      redirect: modules[0].routes[0].path,
      auth: { allowAnonymous: true },
    },
  ];

  for (const route of routes) {
    router.addRoute(route);
  }

  router.beforeEach(async (to, from, next) => {
    const currentMaintenance = await Maintenance.getCurrent();
    if (
      currentMaintenance &&
      // allow maintenance bypass by adding ?maintenance=bypass in URL
      !(to.query.maintenance && to.query.maintenance === "bypass") &&
      to.path !== "/maintenance"
    ) {
      return next("/maintenance");
    }
    if (!currentMaintenance && to.path === "/maintenance") {
      return next("/");
    }

    if (!allowsAnonymous(to)) {
      if (!(await Auth.ensureAuthenticated())) {
        return; // break-out of it, `Auth.ensureAuthenticated` has set `window.location.href`
      }
    }

    if (!isAuthorized(to)) {
      return next("/forbidden");
    }

    // setLocale placed here to avoid vue-i18 warn on load
    // [vue-i18n] Cannot translate the value of keypath '...'. Use the value of keypath as default.
    // see doc: https://kazupon.github.io/vue-i18n/guide/lazy-loading.html
    if (Auth.claims && !(i18n.locale in i18n.messages)) {
      return await setLocale(Auth.claims["PrefLanguage"], true).then(() => next());
    }
    return next();
  });

  return router;
}

function allowsAnonymous(route: Route) {
  return matchesAuth(route, auth => (auth && auth.allowAnonymous) || false);
}

function isAuthorized(route: Route) {
  return matchesAuth(route, auth => !auth || !auth.roles || auth.roles.length == 0 || Auth.isInRole(...auth.roles));
}

function matchesAuth(route: Route, predicate: (auth: RouteAuthConfig | null) => boolean) {
  return route.matched.some(r => {
    const auth = r.meta?.auth as RouteAuthConfig;
    return predicate(auth);
  });
}

function protectByRouteAuthConfig(r: PortalRouteConfig | RouteConfig, auth: RouteAuthConfig) {
  r.meta = r.meta || {};
  r.meta.auth = auth;
  if (r.children) {
    r.children.map(child => {
      protectByRouteAuthConfig(child, auth);
    });
  }
}
